import { useTranslation } from "react-i18next";
import clsx from "clsx";
import useClickAway from "@/core/helpers/useClickAway";
import { CrossIcon } from "@/shared/icons";
import { Button, SoundButton } from "@/shared/ui";

type Props = {
  id: number;
  name: string;
  address: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  isConfirming: boolean;
};

export default function HotelRegistrationModal({
  id,
  name,
  address,
  onClose,
  onConfirm,
  isLoading,
  isConfirming,
}: Props) {
  const { t } = useTranslation("hotel");

  const ref = useClickAway(onClose);

  return (
    <div
      className={clsx(
        "absolute z-10 top-0 left-0 right-0 bottom-0 bg-[#0F0F0F]/95",
        "flex items-center justify-center",
        "animate-[--animation-fade-in]",
      )}
      style={{ backdropFilter: "blur(2px)" }}
      data-mz-accent="green"
    >
      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="mz-loader" />
        </div>
      )}

      {!isLoading && (
        <div
          ref={ref}
          className={clsx(
            "w-[25.125rem] p-5 bg-gradient-to-b from-[#0F0F0F] to-[#0F0F0F]/90 rounded-[0.875rem]",
            "relative p-7.5 flex flex-col items-center greenShadow30",
          )}
        >
          <SoundButton
            className="absolute top-2.5 right-2.5 w-[1.875rem] h-[1.875rem] p-0 bg-dark-800"
            variant="soft"
            onClick={onClose}
          >
            <CrossIcon className="size-3.5" />
          </SoundButton>
          <p className="text-18 font-bold">{name}</p>
          <p className="text-15 font-madium text-white/70">
            {address}, #{id}
          </p>
          <div className="h-[1px] w-[20rem] bg-[#1F1F1F]/80 flex justify-center my-5">
            <div className="h-full w-[5rem] bg-accent-solid" />
          </div>
          <div className="mb-5 text-16 font-medium text-center">
            {t("hotel.registrationConfirmation.question")}
          </div>
          <Button
            className="w-full h-11"
            onClick={onConfirm}
            loading={isConfirming}
            disabled={isConfirming}
          >
            {t("hotel.checkOutConfirmation.confirm")}
          </Button>
        </div>
      )}
    </div>
  );
}
